/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

// import LeftRail from '@cchome/home-left-rail/src/containers/LeftRail';
import {DeviceDetectionUtil} from 'cchome-shared/utils';
import {DiscoverAllTab, DiscoverCategoryTab, DiscoverUtils} from 'discover-hub';
import React, {Component} from 'react';
import {injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import DiscoverAnalytics from '../../../utils/DiscoverAnalytics';
import LearnDiscover from '../Learn/LearnDiscover';
import './Discover.scss';
import DiscoverLeftNav from './DiscoverLeftNav';

declare let adobeIMS: AdobeIMS;
const analytics = new DiscoverAnalytics();

type Props = {
	env: string;
	locale: string;
	route: any;
	match: any;
	history: any;
	setOverwriteComponent: Function;
	location: any;
	intl: any;
	leftRailOpen: boolean;
};

type State = {
	pageIndex: number;
	topic: string;
	category: string;
};
class DiscoverPage extends Component<Props, State> {
	constructor(props) {
		super(props);

		this.state = {
			pageIndex: DiscoverPage.getPageIndex(this.props.location),
			topic: DiscoverPage.getTopic(this.props.location),
			category: DiscoverPage.getNav(this.props.location),
		};
	}

	componentDidMount() {
		const callbackHandler = (tab) => {
			this.props.history.push(`/cc/discover/${tab}`);
		};

		this.hideJarvis();
		if (DeviceDetectionUtil.isInternetExplorer()) {
			this.props.setOverwriteComponent(null);
		}
		else {
			this.props.setOverwriteComponent(
				<DiscoverLeftNav
					items={DiscoverPage.getLeftNavTabs()}
					onDiscoverPathChange={data => this.handleDiscoverPathChange(data)}
					currentTab={this.state.category}
					postAnalytics={data => analytics.postCustomData(data)}
				/>,
			);
		}
	}

	/**
	 * Returns an array of data for each left tab nav. Each tab gets information
	 * about it's key and it's localized label.
	 * @returns {Array<object>}
	 */
	static getLeftNavTabs() {
		let tabs = DiscoverUtils.filterTypes.map(filterType => ({
			key: filterType,
			label: `discover.${filterType}`,
		}));

		if (!window.cchome.util.hasFeature('LivestreamTab')) {
			tabs = tabs.filter(tab => tab.key !== 'livestreams');
		}
		if (!window.cchome.util.hasFeature('discover_career_resources')) {
			tabs = tabs.filter(tab => tab.key !== 'career-resources');
		}
		return tabs;
	}

	componentDidUpdate(prevProps, prevState) {
		// Need to notify component to update it's props
		if (prevState.category !== this.state.category) {
			if (!DeviceDetectionUtil.isInternetExplorer()) {
				this.props.setOverwriteComponent(
					<DiscoverLeftNav
						items={DiscoverPage.getLeftNavTabs()}
						onDiscoverPathChange={data => this.handleDiscoverPathChange(data)}
						currentTab={this.state.category}
						postAnalytics={data => analytics.postCustomData(data)}
					/>,
				);
			}
		}
		if (prevProps.location !== this.props.location) {
			const nextTopic = DiscoverPage.getTopic(this.props.location);
			const nextPageIndex = DiscoverPage.getPageIndex(this.props.location);
			const nextCategory = DiscoverPage.getNav(this.props.location);

			if (nextTopic !== this.state.topic) {
				// eslint-disable-next-line react/no-did-update-set-state
				this.setState({topic: nextTopic});
			}
			if (nextPageIndex !== this.state.pageIndex) {
				// eslint-disable-next-line react/no-did-update-set-state
				this.setState({pageIndex: nextPageIndex});
			}
			if (nextCategory !== this.state.category) {
				// eslint-disable-next-line react/no-did-update-set-state
				this.setState({category: nextCategory});
			}
		}
		// if location changes set index and topic
	}
	componentWillUnmount() {
		this.showJarvis();
		this.props.setOverwriteComponent(null);
	}

	showJarvis() {
		document.body && document.body.classList.remove('hide-jarvis');
	}

	hideJarvis() {
		document.body && document.body.classList.add('hide-jarvis');
	}

	/**
	 * Given the page's path, extracts the page number and then turns it into an index.
	 * ex. page=2 --> 1
	 * @param {object} location
	 */
	static getPageIndex(location) {
		return DiscoverUtils.getSearchParams(location.search).page - 1 || 0;
	}

	static getTopic(location) {
		return DiscoverUtils.getSearchParams(location.search).filter || 'all-topics';
	}

	static getNav(location) {
		const afterDiscover = location.pathname.split('/discover')[1];
		let nav
			= afterDiscover !== '' && afterDiscover.split('/').length > 0 ? afterDiscover.split('/')[1] : 'featured';
		nav = nav === '' ? 'featured' : nav;
		return nav;
	}

	/**
	 * Given the path of the URL, determines
	 * what the current tab category is.
	 *
	 * @returns {string} category -- [learn, how-to, challenges, livestreams,
	 * inspiration, free-stuff]
	 */
	getCurrentTab() {
		let currentTab = '';

		const path = this.props.match.url.split('/discover')[1].split('/');

		// Just in case user does some kind of garbage cc/discover/xxx
		const allowedTabs = DiscoverUtils.filterTypes.map(filterType => DiscoverUtils.filterTypePath(filterType));
		if (path[1] && allowedTabs.includes(path[1])) {
			currentTab = path[1];
		}

		return currentTab;
	}

	/**
	 * Callback function to be passed into discover-hub
	 * component to allow route changes.
	 *
	 * @param {any} arg1
	 * @param {bool} isArticle
	 */
	handleDiscoverPathChange(arg1) {
		// This is an array, contain the signals
		// [{type: "topic", value: "photography"},
		// {type: "pageIndex", value: 1},
		// {type: "discoverCategory", value: "inspiration"}]
		const signals = arg1;
		let newPath = this.props.location.pathname; // ex. /cc/discover/inspiration
		const newState: any = {};

		let isArticle = false; // TODO: Maybe there's another better way to do this
		let isVideo = false;
		const newSearchParams = signals.reduce((params, signal) => {
			if (signal.type === 'topic') {
				// ..?topic=photography
				newState.topic = signal.value;

				if (signal.value !== 'all-topics') {
					params.filter = signal.value;
				}
			}
			else if (signal.type === 'pageIndex') {
				// ..?page=1
				newState.pageIndex = signal.value;

				if (signal.value !== 0) {
					params.page = signal.value + 1;
				}
			}
			else if (signal.type === 'discoverCategory') {
				// cc/en/discover/inspiration
				const beforeDiscover = newPath.split('/discover')[0];
				const extraPath = signal.value === 'featured' ? '' : `${signal.value}/`;
				newPath = `${beforeDiscover}/discover/${extraPath}`;
				newState.category = signal.value;
			}
			else if (signal.type === 'article') {
				// push this path with go to a different route
				const beforeDiscover = newPath.split('/discover')[0];
				const extraPath = `${signal.value.articleSlug}`;
				newPath = `${beforeDiscover}/discover/article/${extraPath}`; // NOTE: This drops all query params

				isArticle = true;
			}
			else if (signal.type === 'video') {
				const beforeDiscover = newPath.split('/discover')[0];
				const extraPath = `${signal.value.videoSlug}/${signal.value.videoId}`;
				newPath = `${beforeDiscover}/discover/video/${extraPath}`;

				isVideo = true;
			}
			return params;
		}, {});

		if (isArticle || isVideo) {
			this.props.history.push({
				pathname: `${newPath}`,
				state: {
					currentTab: this.getCurrentTab(),
					topic: this.state.topic,
				},
				onDiscoverPathChange: this.handleDiscoverPathChange.bind(this),
			});
		}
		else {
			this.setState(newState);
			this.props.history.push(`${newPath}${DiscoverUtils.getSearchPath(newSearchParams)}`);
		}
	}

	handleRouteChange = (route: string) => {
		this.props.history.push(route);
	};

	/**
	 * Returns IMS Data required for making authorized server requests for discover-hub
	 * @returns {object}
	 */
	getIMSData() {
		return {
			bearerToken: adobeIMS.getAccessToken(),
			// @ts-ignore
			userId: window.cchome.util.getUserId(),
		};
	}

	categoryTab() {
		const currentTab = this.getCurrentTab();
		let tab;
		const imsData = this.getIMSData();
		if (currentTab === 'learn') {
			tab = (
				<LearnDiscover
					clientID={'CCHomeWeb1'}
					env={this.props.env}
					locale={this.props.locale}
					optinInited
					match={this.props.match}
					location={this.props.location}
					setOverwriteComponent={() => {}}
					history={this.props.history}
					onDiscoverPathChange={data => this.handleDiscoverPathChange(data)}
					intl={this.props.intl}
					pageIndex={this.state.pageIndex}
				/>
			);
		}
		else if (currentTab !== '') {
			tab = (
				<div className='discover-category-tab-container'>
					<DiscoverCategoryTab
						featureFlags={window.cchome.util.getAllFeatures()}
						env={this.props.env}
						imsData={imsData}
						locale={this.props.locale}
						categoryType={this.state.category}
						filterTopic={this.state.topic}
						pageIndex={this.state.pageIndex}
						path={`/${currentTab}`}
						signedIn
						location={this.props.location}
						onDiscoverPathChange={data => this.handleDiscoverPathChange(data)}
						postAnalytics={data => analytics.postCustomData(data)}
						intl={this.props.intl}
						logger={window.cchome.util.logger}
					/>
				</div>
			);
		}
		else {
			tab = (
				<div className='discover-category-tab-container'>
					<DiscoverAllTab
						featureFlags={window.cchome.util.getAllFeatures()}
						env={this.props.env}
						imsData={imsData}
						locale={this.props.locale}
						location={this.props.location}
						filterTopic={this.state.topic}
						path={'/'}
						signedIn
						onDiscoverPathChange={data => this.handleDiscoverPathChange(data)}
						postAnalytics={data => analytics.postCustomData(data)}
						intl={this.props.intl}
						logger={window.cchome.util.logger}
					/>
				</div>
			);
		}
		return tab;
	}

	render() {
		if (DeviceDetectionUtil.isInternetExplorer()) {
			return (
				<div className='ie-error-page'>
					<p>{this.props.intl.formatMessage({id: 'app.ie11.error.message'})}</p>
				</div>
			);
		}

		return (
			<>
				{/* {hasLeftRail && (
					<LeftRail open={this.props.leftRailOpen} handleRouteChange={this.handleRouteChange} overlay />
				)} */}
				<div style={{position: 'relative'}}>{this.categoryTab()}</div>
			</>
		);
	}
}

function mapStateToProps(state) {
	return {
		learnapps: state,
		optinInited: state.app.analyticsOptinInited,
		leftRailOpen: state.uistore.leftRailOpen,
	};
}

export default connect(mapStateToProps, {})(injectIntl(DiscoverPage));
